import { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { X, AlertTriangle, ShieldCheck, Lock, NotebookPen } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { fmtMoney, fmtSignedPercent } from '@/lib/format';
import { cn } from '@/lib/cn';
import type { Violation } from '@t212/shared';

const SETUPS = ['swing', 'long-term', 'speculative', 'income', 'hedge'] as const;
type Setup = (typeof SETUPS)[number];

type Props = {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  ticker: string;
  defaultPrice: number;
  defaultSide?: 'BUY' | 'SELL';
};

export function OrderPreviewDialog({ open, onOpenChange, ticker, defaultPrice, defaultSide = 'BUY' }: Props) {
  const qc = useQueryClient();
  const [side, setSide] = useState<'BUY' | 'SELL'>(defaultSide);
  const [type, setType] = useState<'MARKET' | 'LIMIT' | 'STOP' | 'STOP_LIMIT'>('MARKET');
  const [quantity, setQuantity] = useState(1);
  const [limitPrice, setLimitPrice] = useState(defaultPrice);
  const [stopPrice, setStopPrice] = useState(defaultPrice * 0.95);
  const [targetPrice, setTargetPrice] = useState(defaultPrice * 1.1);
  const [rationale, setRationale] = useState('');
  const [setupTag, setSetupTag] = useState<Setup | null>(null);
  const [maxRiskPct, setMaxRiskPct] = useState(1.0);

  // R-multiple math for the pre-trade plan
  const oneR = Math.abs(defaultPrice - stopPrice);
  const targetR = oneR > 0 ? Math.abs(targetPrice - defaultPrice) / oneR : 0;
  const riskNotional = quantity * oneR;

  const preview = useMutation({
    mutationFn: () =>
      api.previewOrder({
        ticker,
        side,
        type,
        quantity,
        ...(type === 'LIMIT' || type === 'STOP_LIMIT' ? { limitPrice } : {}),
        ...(type === 'STOP' || type === 'STOP_LIMIT' ? { stopPrice } : {}),
      }),
  });

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-zinc-950/80 backdrop-blur-sm z-50 data-[state=open]:animate-in data-[state=open]:fade-in" />
        <Dialog.Content className="fixed right-0 top-0 bottom-0 z-50 w-full sm:w-[440px] bg-zinc-950 border-l border-zinc-800 p-5 overflow-y-auto data-[state=open]:animate-in data-[state=open]:slide-in-from-right">
          <div className="flex items-center justify-between mb-5">
            <Dialog.Title className="text-sm font-semibold text-zinc-100 flex items-center gap-2">
              <Lock className="size-4 text-amber-400" /> Order preview
            </Dialog.Title>
            <Dialog.Close className="text-zinc-400 hover:text-zinc-200">
              <X className="size-4" />
            </Dialog.Close>
          </div>

          <div className="text-[11px] text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded-md px-3 py-2 mb-4">
            Preview mode. Orders are not placed live. Phase 4 — manual order placement — is wired
            for review only.
          </div>

          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <Btn active={side === 'BUY'} onClick={() => setSide('BUY')}>Buy</Btn>
              <Btn active={side === 'SELL'} onClick={() => setSide('SELL')}>Sell</Btn>
            </div>

            <div className="grid grid-cols-4 gap-1.5 text-xs">
              {(['MARKET', 'LIMIT', 'STOP', 'STOP_LIMIT'] as const).map((t) => (
                <button
                  type="button"
                  key={t}
                  onClick={() => setType(t)}
                  className={cn(
                    'px-2 py-1.5 rounded-md border',
                    type === t
                      ? 'border-indigo-500/60 bg-indigo-500/15 text-indigo-100'
                      : 'border-zinc-800 text-zinc-400 hover:text-zinc-200',
                  )}
                >
                  {t}
                </button>
              ))}
            </div>

            <Field label="Quantity">
              <input
                type="number"
                inputMode="decimal"
                min={0}
                step={1}
                value={quantity}
                onChange={(e) => setQuantity(Number(e.target.value))}
                className="bg-transparent outline-none text-sm w-full num"
              />
            </Field>

            {(type === 'LIMIT' || type === 'STOP_LIMIT') && (
              <Field label="Limit price">
                <input
                  type="number"
                inputMode="decimal"
                  step="0.01"
                  value={limitPrice}
                  onChange={(e) => setLimitPrice(Number(e.target.value))}
                  className="bg-transparent outline-none text-sm w-full num"
                />
              </Field>
            )}
            {(type === 'STOP' || type === 'STOP_LIMIT') && (
              <Field label="Stop price">
                <input
                  type="number"
                inputMode="decimal"
                  step="0.01"
                  value={stopPrice}
                  onChange={(e) => setStopPrice(Number(e.target.value))}
                  className="bg-transparent outline-none text-sm w-full num"
                />
              </Field>
            )}

            <div className="rounded-lg border border-zinc-800 bg-zinc-900/30 p-3 space-y-2">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-zinc-400">
                <NotebookPen className="size-3.5 text-indigo-300" /> Pre-trade plan
              </div>

              <Field label="Target price">
                <input
                  type="number"
                inputMode="decimal"
                  step="0.01"
                  value={targetPrice}
                  onChange={(e) => setTargetPrice(Number(e.target.value))}
                  className="bg-transparent outline-none text-sm w-full num"
                />
              </Field>

              <Field label="Max risk %">
                <input
                  type="number"
                inputMode="decimal"
                  step="0.1"
                  min={0.05}
                  max={10}
                  value={maxRiskPct}
                  onChange={(e) => setMaxRiskPct(Number(e.target.value))}
                  className="bg-transparent outline-none text-sm w-full num"
                />
              </Field>

              <div className="flex items-center justify-between text-[11px]">
                <span className="text-zinc-400">Risk × R</span>
                <span className="num text-zinc-200">
                  {fmtMoney(riskNotional)} · {targetR.toFixed(1)}R target
                </span>
              </div>

              <div className="flex flex-wrap gap-1 pt-1">
                {SETUPS.map((s) => (
                  <button
                    type="button"
                    key={s}
                    onClick={() => setSetupTag(setupTag === s ? null : s)}
                    className={cn(
                      'text-[11px] px-2 py-0.5 rounded-full border capitalize transition-colors',
                      setupTag === s
                        ? 'border-indigo-500/60 bg-indigo-500/15 text-indigo-100'
                        : 'border-zinc-800 text-zinc-400 hover:text-zinc-200',
                    )}
                  >
                    {s}
                  </button>
                ))}
              </div>

              <textarea
                value={rationale}
                onChange={(e) => setRationale(e.target.value)}
                placeholder="Why this trade? Stop logic, target rationale, what would invalidate the idea."
                rows={3}
                className="w-full bg-zinc-950 border border-zinc-800 rounded p-2 text-xs text-zinc-100 outline-none focus:border-indigo-500/60 placeholder:text-zinc-400 resize-none"
              />
            </div>

            <button
              type="button"
              onClick={() => {
                preview.mutate(undefined, {
                  onSuccess: () => qc.invalidateQueries({ queryKey: ['violations'] }),
                  onError: () => toast.error('Preview failed'),
                });
                // Persist plan to journal so it survives the dialog close.
                if (rationale.trim() || setupTag) {
                  api
                    .putJournal(ticker, {
                      rationale: rationale.trim()
                        ? `${rationale.trim()}\n\nPlan (${new Date().toISOString().slice(0, 10)}): ${side} ${quantity} @ ${defaultPrice.toFixed(2)} · stop ${stopPrice.toFixed(2)} · target ${targetPrice.toFixed(2)} · max risk ${maxRiskPct}%`
                        : '',
                      setupTag,
                    })
                    .catch(() => {
                      /* non-fatal */
                    });
                }
              }}
              disabled={preview.isPending}
              className="w-full px-3 py-2 rounded-md bg-indigo-500 hover:bg-indigo-400 text-zinc-950 font-medium text-sm transition-colors disabled:opacity-60"
            >
              {preview.isPending ? 'Computing…' : 'Run pre-flight + save plan'}
            </button>

            {preview.data && (
              <div className="mt-2 rounded-lg border border-zinc-800 bg-zinc-950 p-3 space-y-2">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-zinc-400">Notional</span>
                  <span className="num">{fmtMoney(preview.data.estimatedNotional)}</span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-zinc-400">Cash after</span>
                  <span
                    className={cn(
                      'num',
                      preview.data.cashAfter < 0 ? 'text-rose-400' : 'text-zinc-200',
                    )}
                  >
                    {fmtMoney(preview.data.cashAfter)}
                  </span>
                </div>

                <div className="border-t border-zinc-800 pt-2">
                  {preview.data.warnings.length > 0 &&
                    preview.data.warnings.map((w, i) => (
                      <div
                        key={i}
                        className="flex items-start gap-2 text-xs text-rose-300 mb-1.5"
                      >
                        <AlertTriangle className="size-3.5 mt-0.5" />
                        <span>{w}</span>
                      </div>
                    ))}

                  {preview.data.preflightViolations.length === 0 ? (
                    <div className="flex items-center gap-2 text-xs text-emerald-400">
                      <ShieldCheck className="size-3.5" /> All rules pass.
                    </div>
                  ) : (
                    <div className="space-y-1.5">
                      <div className="text-xs text-zinc-400">Rule effects:</div>
                      {preview.data.preflightViolations.map((v: Violation, i: number) => (
                        <div
                          key={i}
                          className={cn(
                            'text-[11px] px-2 py-1 rounded border',
                            v.severity === 'block'
                              ? 'border-rose-500/40 bg-rose-500/10 text-rose-200'
                              : v.severity === 'warn'
                                ? 'border-amber-500/40 bg-amber-500/10 text-amber-200'
                                : 'border-indigo-500/40 bg-indigo-500/10 text-indigo-200',
                          )}
                        >
                          {v.message}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <button
                  type="button"
                  disabled
                  title="Live placement is intentionally disabled at MVP"
                  className="w-full px-3 py-2 mt-2 rounded-md border border-zinc-800 text-zinc-400 cursor-not-allowed text-sm flex items-center justify-center gap-2"
                >
                  <Lock className="size-3.5" /> Live placement disabled
                </button>
              </div>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function Btn({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'px-3 py-2 rounded-md border text-sm font-medium transition-colors',
        active
          ? 'border-indigo-500/60 bg-indigo-500/20 text-indigo-100'
          : 'border-zinc-800 text-zinc-400 hover:text-zinc-200',
      )}
    >
      {children}
    </button>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="flex items-center gap-2 px-3 py-2 rounded-md border border-zinc-800 bg-zinc-950">
      <span className="text-xs text-zinc-400 w-24 shrink-0">{label}</span>
      {children}
    </label>
  );
}
